import Vue from 'vue'
import axios from 'axios'
import router from '../router'
import { store } from '../store/store'
import { loadCompanyIntegrations } from './companyIntegrations'
import { resetSessionExpiryHandling } from './sessionExpiry'

export default {
  check (context, subCheck = true) {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem('token') && localStorage.getItem('token') != null) {
        // Check if context has required methods and store
        if (!context.showOverlay || !context.closeOverlay) {
          console.warn('Auth check: context missing required methods');
          reject(new Error('Context missing required methods'));
          return;
        }
        
        // Safely access store - use context.$store if available, fallback to imported store
        const storeInstance = context.$store || store;
        if (!storeInstance) {
          console.warn('Auth check: no store available');
          reject(new Error('No store available'));
          return;
        }
        
        context.showOverlay()
        
        // Ensure the token is set in axios headers
        const token = localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        }
        
        axios.get('/api/auth/user').then(response => {
          if (response.data.user != null) {
            storeInstance.commit('user/setLoggedIn')
            storeInstance.commit('user/setUserProfile', response.data.user)
            storeInstance.commit('user/setUserSubscription', response.data.subscription)

            // Smardie platform admin — no subscription / upgrade checks
            const isPlatformAdmin = storeInstance.getters && storeInstance.getters['user/isPlatformAdmin']
            if (isPlatformAdmin) {
              context.closeOverlay()
              resolve(response.data.user)
              return
            }

            // For non-admins, always refresh subscription status so header/router know if upgrade is required
            return axios.get('/subscription/status').then((statusRes) => {
              storeInstance.commit('setSubscriptionStatus', statusRes.data)
              return loadCompanyIntegrations(storeInstance).then(() => {
                if (subCheck === true && statusRes.data && statusRes.data.must_upgrade) {
                  context.$router.push('/settings/plans')
                }
                context.closeOverlay()
                resolve(response.data.user)
              })
            }).catch((statusErr) => {
              console.error('Subscription status check failed:', statusErr)
              storeInstance.commit('setSubscriptionStatus', null)
              context.closeOverlay()
              resolve(response.data.user)
            })
          } else {
            this.logout(context)
            reject(new Error('No user data'));
          }
        }).catch((exception) => {
          this.logout(context)
          context.closeOverlay()
          reject(exception);
        })
      } else {
        this.logout(context)
        reject(new Error('No token'));
      }
    });
  },
  login (context, email, password) {
    const storeInstance = context.$store || store;
    
    // Set login flag to disable navigation guard
    // Clear stale session so login is not sent with an old Bearer token
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']

    storeInstance.commit('user/setLoginInProgress', true)
    resetSessionExpiryHandling()

    const normalizedEmail = String(email || '').trim()
    const normalizedPassword = String(password || '')
    if (!normalizedEmail || !normalizedPassword) {
      storeInstance.commit('user/setLoginInProgress', false)
      context.errorNotification('Please enter your email and password.')
      return
    }
    
    axios.post('/api/auth/login', {
      'email': normalizedEmail,
      'password': normalizedPassword
    }).then((response) => {
      axios.defaults.headers.common = {
        'Authorization': 'Bearer ' + response.data.token
      }
      
      if (storeInstance) {
        storeInstance.commit('user/setLoggedIn')
      }
      localStorage.setItem('token', response.data.token)
      
      if (response.data.requires_account_selection) {
        storeInstance.commit('user/setPendingAccountSelection', true)
      } else {
        storeInstance.commit('user/setPendingAccountSelection', false)
      }

      // Get user profile first, then handle initial routing
      this.check(context, false).then(() => {
        // After profile is loaded, handle initial routing based on role
        let pendingXero = null
        try {
          const raw = sessionStorage.getItem('smardie_xero_oauth')
          if (raw) {
            pendingXero = JSON.parse(raw)
            sessionStorage.removeItem('smardie_xero_oauth')
          }
        } catch (e) {
          pendingXero = null
        }
        if (response.data.requires_account_selection) {
          context.$router.push('/select-account')
        } else if (pendingXero && pendingXero.xero) {
          context.$router.push({ path: '/settings/account', query: pendingXero })
        } else if (storeInstance && storeInstance.getters['user/isPlatformAdmin']) {
          context.$router.push('/settings/category')
        } else {
          context.$router.push('/')
        }
        // Clear the login flag after routing is complete
        setTimeout(() => {
          storeInstance.commit('user/setLoginInProgress', false)
        }, 100)
      }).catch((checkErr) => {
        storeInstance.commit('user/setLoginInProgress', false)
        console.error('Post-login session check failed:', checkErr)
        context.errorNotification('Login succeeded but session could not be loaded. Please try again.')
      })
    }).catch((exception) => {
      // Clear the login flag on error
      storeInstance.commit('user/setLoginInProgress', false)
      if (exception.response) {
        if (exception.response.status === 401) {
          context.errorNotification('Invalid login details!')
        } else if (exception.response.data && exception.response.data.error) {
          context.errorNotification(exception.response.data.error)
        } else {
          context.errorNotification('Login failed. Please try again.')
        }
      } else if (exception.request) {
        context.errorNotification('Unable to reach server. Please check your connection and try again.')
      } else {
        context.errorNotification('Login failed. Please try again.')
      }
    })
  },
  logout (context) {
    // Safely access store - use context.$store if available, fallback to imported store
    const storeInstance = context && context.$store ? context.$store : store;
    if (storeInstance) {
      storeInstance.commit('user/setLoggedOut')
      storeInstance.commit('user/setPendingAccountSelection', false)
    }
    localStorage.removeItem('token', null)
    if (context && context.$router) {
      context.$router.push('/login')
    } else if (router) {
      router.push('/login')
    }
  }
}
